import {
  createTourTranslationsConfig,
  type TourTranslation,
} from '@/config/objects/tour-translations';
import { useGenerateTourTranslations } from '@/hooks/useGenerateTourTranslations';
import { GenerateTranslationsDialog } from '@/components/shared/GenerateTranslationsDialog';
import { CrudListView } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TourTranslationsListProps {
  tourId: string;
  className?: string;
}

/**
 * Displays all translations for a tour using standard CrudListView
 */
export function TourTranslationsList({
  tourId,
  className,
}: TourTranslationsListProps) {
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const generateMutation = useGenerateTourTranslations();
  const navigate = useNavigate();

  const handleGenerateTranslations = useCallback(() => {
    setGenerateDialogOpen(true);
  }, []);

  const translationsConfig = useMemo(
    () =>
      createTourTranslationsConfig(tourId, {
        onGenerateTranslations: handleGenerateTranslations,
      }),
    [tourId, handleGenerateTranslations],
  );

  const handleItemClick = useCallback(
    (translation: TourTranslation) => {
      navigate(`/tours/${tourId}/translations/${translation.id}`);
    },
    [tourId, navigate],
  );

  return (
    <>
      <div className={className}>
        <CrudListView
          config={translationsConfig}
          baseUrl={`/tours/${tourId}/translations`}
          onItemClick={handleItemClick}
          getValueFromItem={(translation: TourTranslation) => translation.id}
          getLabelFromItem={(translation: TourTranslation) =>
            `${translation.language?.name || 'Unknown'} - ${translation.name}`
          }
          headerActions={[
            {
              key: 'generate',
              label: 'Generate Translations',
              icon: Languages,
              variant: 'default' as const,
              onClick: () => setGenerateDialogOpen(true),
              loading: generateMutation.isPending,
            },
          ]}
        />
      </div>

      <GenerateTranslationsDialog
        open={generateDialogOpen}
        onClose={() => {
          setGenerateDialogOpen(false);
        }}
        title="Generate Translations"
        description="Select languages to translate this tour to. Existing translations will be skipped."
        onGenerate={(selectedLanguageIds) => {
          generateMutation.mutate(
            {
              tourId,
              targetLanguageIds: selectedLanguageIds,
            },
            {
              onSuccess: () => {
                setGenerateDialogOpen(false);
              },
            },
          );
        }}
        isGenerating={generateMutation.isPending}
      />
    </>
  );
}
